import React from 'react';
import {WithTranslation, withTranslation} from 'react-i18next';
import {services} from '../../../../application/service/services';
import {VisualizationInstance} from '../../../model/viz/VisualizationInstance';
import './distributionViz.scss';
import {ClickableViz} from "../ClickableViz";
import {
    FieldVizFeedbacksQueryContext,
    MultiFieldsVizFeedbacksQueryContext,
    VizFeedbacksQueryContext
} from '../../../model/viz/VizFeedbacksQueryContext';
import {connect} from "react-redux";
import {FieldValue} from "../../../../application/model/field/FieldValue";
import {TableItem, TableItemUI, TableUIData} from "../table/AbstractTableViz";
import {DistributionPanel} from "../../components/distribution/DistributionPanel";
import {Button} from "@blueprintjs/core";
import _ from "lodash";
import {DataRenderer} from "../../../../component/table/DataRenderer";

interface DistributionVizProps extends WithTranslation {
    viz: VisualizationInstance,
    openFeedbacksPanel: (viz: ClickableViz, panelTitle: string) => void
}

interface StateProps {
    clickItem: TableItemUI,
    detailedMode: boolean,
    netSentimentMode: boolean
}


export class DistributionVizComponent extends React.Component<DistributionVizProps> implements ClickableViz {

    state: StateProps = {
        clickItem: null,
        detailedMode: false,
        netSentimentMode: false
    }


    onClickItem(newlySelectedItem: TableItemUI, event: MouseEvent) {
        this.state.clickItem = newlySelectedItem;
        const {t, viz} = this.props;
        const field = services.getFieldsService().getField(newlySelectedItem.field);
        const fieldValue = services.getFieldsService().findFieldValue(field.name, newlySelectedItem.fieldValue);
        this.props.openFeedbacksPanel(this, fieldValue ? fieldValue.getLabel(t) : newlySelectedItem.fieldValue);
        event.preventDefault();
        event.stopPropagation();
    };

    getFeedBacksQueryContext(): VizFeedbacksQueryContext {
        const result = new MultiFieldsVizFeedbacksQueryContext();
        if (this.state.clickItem) {
            const {viz} = this.props;
            result.fields = this.getFieldsFromItem(this.state.clickItem);
        }
        return result;
    }

    private getFieldsFromItem(clickItem: any): Array<FieldVizFeedbacksQueryContext> {
        const result = [];
        if (clickItem.parent) {
            result.push(...this.getFieldsFromItem(clickItem.parent));
        }
        const field = new FieldVizFeedbacksQueryContext();
        field.field = clickItem.field;
        field.selectedValue = clickItem.fieldValue;
        result.push(field);
        return result;
    }

    getClickedFieldValue(): FieldValue {
        if (this.state.clickItem) {
            const {viz} = this.props;
            return services.getFieldsService().findFieldValue(viz.definition.params.field, this.state.clickItem.fieldValue);
        }
        return null;
    }


    render() {
        const {t, viz} = this.props;
        const data = viz.data as TableUIData;
        const uiParams = viz.definition.uiParams;
        const is2Levels = viz.definition.params.columns.filter(column => column["@type"] === "aggregation").length > 1;
        const tonalities = viz.definition.params.columns.filter(column => column["@type"] === "tonalities").length > 0;

        return <div className="distribution">

            {is2Levels ?
                <>
                    <div className="buttons">
                        <Button disabled={!this.state.detailedMode} onClick={() => {
                            this.setState({detailedMode: false})
                        }}
                                text={t('dashboard.viz.distribution.btn.synthetic')}/>
                        <Button disabled={this.state.detailedMode} onClick={() => {
                            this.setState({detailedMode: true})
                        }}
                                text={t('dashboard.viz.distribution.btn.detailed')}/>
                    </div>
                    {this.get2LevelsHeadline()}
                </>
                : (tonalities ?
                    <>
                        <div className="buttons">
                            <Button disabled={!this.state.netSentimentMode} onClick={() => {
                                this.setState({netSentimentMode: false})
                            }}
                                    text={t('dashboard.viz.distribution.btn.volume')}/>
                            <Button disabled={this.state.netSentimentMode} onClick={() => {
                                this.setState({netSentimentMode: true})
                            }}
                                    text={t('dashboard.viz.distribution.btn.netSentiment')}/>
                        </div>
                        {this.state.netSentimentMode ? this.getNetSentimentHeadline() : null}
                    </> : null)

            }
            <DistributionPanel data={data}
                               detailedMode={this.state.detailedMode || !is2Levels}
                               isTwoLevels={is2Levels}
                               showNetSentiment={!is2Levels && this.state.netSentimentMode}
                               showPercent={uiParams.showPercent}
                               showVolume={uiParams.showVolume}
                               showTrend={uiParams.showTrend}
                               onClick={this.onClickItem.bind(this)}
            />
        </div>;
    }

    private get2LevelsHeadline() {

        const {t, viz} = this.props;
        const suffixe = viz.definition.uiParams.suffix;
        const data = viz.data as TableUIData;

        const items = data.items.filter(item => {
            const volume = this.getVolume(item);
            return volume.count > Math.max(50, 0.01 * data.childrenCount) && item.children && item.children.length > 0;
        }).map(item => {
            const childWithMaxVolume = item.children.reduce((max, item) => {
                return this.getVolume(item).percent > this.getVolume(max).percent ? item : max
            });
            return {item, childWithMaxVolume};
        })
            .reduce((itemWithMaxChild, currentValue) => {
                if (!itemWithMaxChild) return currentValue;
                return this.getVolume(itemWithMaxChild.childWithMaxVolume).count > this.getVolume(currentValue.childWithMaxVolume).count ?
                    itemWithMaxChild : currentValue;
            }, null);

        if (items) {
            const field1ValueLbl = services.getFieldsService().findFieldValue(items.item.field, items.item.fieldValue)?.getLabel(t) || items.item.fieldValue;
            const field2ValueLbl = services.getFieldsService().findFieldValue(items.childWithMaxVolume.field, items.childWithMaxVolume.fieldValue)?.getLabel(t) || items.childWithMaxVolume.fieldValue;

            return <div className="distribution-headline" dangerouslySetInnerHTML={{
                __html: t('dashboard.viz.distribution.2LevelsHeadline', {
                    suffixe,
                    percent: _.round(items.childWithMaxVolume ? this.getVolume(items.childWithMaxVolume).percent : 0, 0),
                    field1: field1ValueLbl,
                    field2: field2ValueLbl
                })
            }}>
            </div>;
        }
        return null;
    }

    protected getVolume(item: TableItem): { count: number, percent: number } {
        return item.data.find(
            x => x.name === "volume"
        )?.value;
    }

    private getNetSentimentHeadline() {
        const {t, viz} = this.props;
        const data = viz.data as TableUIData;
        const itemAndNetSentiment = data.items.map(item => {
            const tonalities = item.data.filter(columnValue => columnValue.name === 'tonalities').find(() => true)?.value;
            const volume = this.getVolume(item).count
            const net_sentiment = tonalities ? DataRenderer.getPropertyIgnoreCase(tonalities, 'POSITIVE') - DataRenderer.getPropertyIgnoreCase(tonalities, 'NEGATIVE') : null;
            return {item, net_sentiment, net_sentiment_pondere: net_sentiment * volume};
        })
            .filter(({net_sentiment_pondere}) => net_sentiment_pondere < 0)
            .sort((a, b) => a.net_sentiment_pondere - b.net_sentiment_pondere)
            .slice(0, 1);

        if (itemAndNetSentiment.length > 0) {
            const {item, net_sentiment} = itemAndNetSentiment[0];
            const field = services.getFieldsService().getField(item.field);
            const fieldValue = services.getFieldsService().findFieldValue(field.name, item.fieldValue);
            return <div className="distribution-headline">
                                <span className="description">
                <span
                    className="net-sentiment-headline-item">{fieldValue ? fieldValue.getLabel(t) : item.fieldValue}
                </span>
                <br/>
                <div dangerouslySetInnerHTML={{
                    __html: t('dashboard.viz.distribution.netSentimentHeadline', {
                        netSentiment: net_sentiment,
                        count: this.getVolume(item).count
                    })
                }}/>
             </span>
            </div>;
        }
        return null;
    }
}


const mapDispatchToProps = (dispatch, ownProps) => {
    return {
        openFeedbacksPanel: (viz: ClickableViz, panelTitle: string) => {
            dispatch.dashboard.openFeedBackPanel({viz, panelTitle});
        },
    };
};

export const DistributionViz = withTranslation(undefined, {withRef: true})(connect(null, mapDispatchToProps, null, {forwardRef: true})(DistributionVizComponent));
